import { Form, FormRenderProps, FormSubmitClickEvent } from "@progress/kendo-react-form";
import { Error } from "@progress/kendo-react-labels";
import { Stepper, StepperChangeEvent } from "@progress/kendo-react-layout";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { submitQuote } from "../../api/quotes";
import { getQuoteDetails } from "../../api/vanilla";
import { Commodity, Instrument } from "../../components/quotes";
import VanillaFormRender from "../../components/quotes/vanilla/VanillaFormRender";
import { useApi } from "../../hooks/useApi";
import { useUserContext } from "../../hooks/useUserContext";
import DateUtility from "../../utilities/dateUtilities";
import { StepsInterface } from "./workflow";

export const VanillaWorkflow = () => {
  const { context: user } = useUserContext();
  const {
    roles,
    permissions,
    activeCompany: { features },
  } = user;
  const { state } = useLocation();
  const { data } = state || {};
  const navigate = useNavigate();

  useEffect(() => {
    setStep(0);
    if (!state) navigate("/");
    else if (data?.quoteId) {
      getQuoteDetailsApi.request(data.quoteId);
    } else {
      setFormState({
        quoteName: `${commodity.name} (${commodity.abbreviation}) ${instrument.name}`,
        strikeType: "Fixed",
        direction: 1,
        quoteId: 0,
        commonStrike: true,
        originalAllocationId: null,
      });
    }
  }, []);

  const hasSubmitPermission =
    permissions.canSubmitQuotes && roles.canSubmitQuotes(data?.company.role);
  const hasCredit = data.company.features?.hasCredit;
  const canSubmit = hasSubmitPermission && hasCredit;
  const autoApprove = false; //roles.isRiskTaker && canSubmit;

  const getQuoteDetailsApi = useApi(getQuoteDetails);

  const commodity = data?.commodity ? Commodity.create(data.commodity) : Commodity.EMPTY;
  const instrument = data?.instrument ? Instrument.create(data.instrument) : Instrument.EMPTY;

  const { step: stepString } = useParams();
  const step = parseInt(stepString as string);

  const setStep = (step: number) => {
    navigate(`/quote/vanilla/${step}`, { state });
  };
  const [steps, setSteps] = useState<Array<StepsInterface>>([
    { label: "Start", isValid: undefined },
    { label: "Structure", isValid: undefined },
    { label: "Quote", isValid: undefined },
    { label: "Submit", isValid: undefined },
  ]);

  const [formState, setFormState] = useState<any | null>(null);

  const lastStepIndex = steps.length - 1;
  const isQuoteStep = step === lastStepIndex - 2;
  const isSubmitStep = step === lastStepIndex;
  const isPreviousStepsValid =
    steps.slice(0, step).findIndex((currentStep) => currentStep.isValid === false) === -1;

  useEffect(() => {
    if (!getQuoteDetailsApi.data) return;

    const {
      quote: {
        clientLong,
        configurationId,
        endDate,
        prices,
        quoteName,
        settlementTypeId,
        startDate,
        structureId,
        totalVolume,
        hasCommonStrike,
        hasFixedStrike,
        allocationId,
      },
    } = getQuoteDetailsApi.data;

    const direction = clientLong ? 1 : 2;

    const contractStart = DateUtility.convertToDateWithOffset(
      startDate,
      -1 * instrument.calendarMonthOffset,
    );
    const contractEnd = DateUtility.convertToDateWithOffset(
      endDate,
      -1 * instrument.calendarMonthOffset,
    );

    const newFormState = {
      quoteId: data.quoteId,
      quoteName,
      direction,
      settlement: settlementTypeId,
      configurationId,
      contract: {
        start: contractStart,
        end: contractEnd,
      },
      totalVolume,
      structureId,
      prices,
      commonStrike: hasCommonStrike,
      strikeType: hasFixedStrike ? "Fixed" : "Adder",
      originalAllocationId: allocationId,
    };
    setFormState(newFormState);
  }, [getQuoteDetailsApi.data]);

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (quote: any) => submitQuote(quote.pricingId, quote.quoteId, autoApprove),
    onSuccess: () => {
      navigate(`/?tab=derivatives`);
      queryClient.invalidateQueries({ queryKey: ["quotes"] });
      queryClient.invalidateQueries({ queryKey: ["quote", formState.quoteId] });
    },
  });

  const onStepSubmit = useCallback(
    async (event: FormSubmitClickEvent) => {
      let { isValid, values } = event;

      if (isValid && isSubmitStep && canSubmit) {
        mutation.mutate({ pricingId: values.pricingId, quoteId: values.quoteId });
      } else if (isSubmitStep && isValid) {
        navigate("/?tab=derivatives");
        return;
      }

      const currentSteps = steps.map((currentStep: StepsInterface, index: number) => ({
        ...currentStep,
        isValid: index === step ? isValid : currentStep.isValid,
      }));

      setSteps(currentSteps);

      if (!isValid) return;

      setStep(Math.min(step + 1, lastStepIndex));
      setFormState(values);
    },
    [steps, isQuoteStep, isSubmitStep, step, lastStepIndex],
  );

  const onPrevClick = useCallback(
    (event: any) => {
      event.preventDefault();
      setStep(Math.max(step - 1, 0));
      mutation.reset();
    },
    [step, setStep],
  );

  const handleStepperChange = (e: StepperChangeEvent) => {
    if (e.value < step || steps[e.value].isValid || steps[e.value - 1].isValid) setStep(e.value);
  };

  return (
    <>
      <Stepper
        value={step}
        items={steps}
        onChange={handleStepperChange}
        style={{ padding: "10px 0" }}
      />

      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ flexBasis: "75%" }}>
          {formState && (
            <Form
              initialValues={formState}
              onSubmitClick={onStepSubmit}
              render={(formRenderProps: FormRenderProps) => (
                <VanillaFormRender
                  step={step}
                  commodity={commodity}
                  formRenderProps={formRenderProps}
                  instrument={instrument}
                  steps={steps}
                  onPrevClick={onPrevClick}
                  isSubmitStep={isSubmitStep}
                  isPreviousStepsValid={isPreviousStepsValid}
                  canSubmit={canSubmit}
                  showAnalytics={features.canShowAnalytics}
                  disableSubmit={mutation.isPending}
                  submitLabel={autoApprove ? "Confirm" : undefined}
                />
              )}
            />
          )}
          {mutation.isError && (
            <div className="float-right">
              <Error>Error Submitting Quote</Error>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
